import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading, KPI } from "../components/UI";
import { CreditCard, Plus, X, Download, CheckCircle2, Wallet, AlertCircle, Clock, Shield, Send, Upload, ListChecks } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Payments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceLeases, setInvoiceLeases] = useState([]);
  const [manualPayment, setManualPayment] = useState(null);
  // Bulk-approve state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const reload = async () => {
    const [p, t, l] = await Promise.all([
      api.get("/payments"),
      api.get("/tenants").catch(()=>({data:[]})),
      api.get("/leases").catch(()=>({data:[]}))
    ]);
    setPayments(p.data); setTenants(t.data); setLeases(l.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const createManual = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/payments/manual", {
        tenant_id: f.get("tenant_id") || null,
        lease_id: f.get("lease_id") || null,
        payment_type: f.get("payment_type"),
        amount: Number(f.get("amount")),
        note: f.get("note"),
      });
      toast.success("Manual payment recorded");
      setAdding(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendReminder = async (p) => {
    const target = prompt(
      `Email a payment-due reminder for ${naira(p.amount)} to which address?\n\nLeave blank to use the tenant's email on file:\n${p.tenant?.email || "(no email on tenant record)"}`,
      ""
    );
    if (target === null) return;
    try {
      const { data } = await api.post(`/payments/${p.id}/send-reminder`, { email: (target || "").trim() });
      toast.success(`Reminder emailed to ${data.sent_to}${data.override ? " (custom address)" : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadOnBehalf = async (p, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)"); return; }
    const isPaid = p.payment_status === "paid";
    if (isPaid && !confirm(`This payment is already marked PAID. Attach receipt to the historical record?\n\nThe payment status will stay "paid" — no re-approval needed. This is audit-logged.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/payments/${p.id}/upload-receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        data.historical
          ? `Historical receipt attached to paid payment for ${p.tenant?.full_name || "tenant"}`
          : `Receipt uploaded on behalf of ${p.tenant?.full_name || "tenant"} — pending approval`
      );
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openInvoice = async () => {
    try {
      const { data } = await api.get("/leases");
      setInvoiceLeases(data.filter(l => !["cancelled", "renewed", "expired"].includes(l.lease_status)));
      setInvoicing(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitInvoice = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const payload = {
      lease_id: f.get("lease_id"),
      amount: Number(f.get("amount")),
      payment_type: f.get("payment_type"),
      due_date: f.get("due_date"),
      note: f.get("note") || "",
      email: (f.get("email") || "").trim() || undefined,
    };
    if (!payload.lease_id || !payload.amount || !payload.due_date) {
      toast.error("Lease, amount and due date are required");
      return;
    }
    try {
      const { data } = await api.post("/payments/invoice", payload);
      toast.success(`Invoice for ${naira(data.payment.amount)} emailed to ${data.sent_to}${data.override ? " (custom address)" : ""}`);
      setInvoicing(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markAsPaid = async (id) => {
    const note = prompt("Enter reason for manual override:");
    if (!note) return;
    try { await api.post(`/payments/${id}/manual-record`, { note }); toast.success("Payment marked as paid"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const approvePayment = async (id) => {
    const note = prompt("Approval note (required):");
    if (!note) return;
    try { await api.post(`/payments/${id}/approve`, { note }); toast.success("Payment approved & receipt emailed"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Pending-approval payments are bulk-eligible
  const pendingApprovalIds = payments
    .filter(p => p.payment_status === "pending_approval")
    .map(p => p.id);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      // If every pending row is already selected, clear; else select all of them.
      const allSelected = pendingApprovalIds.length > 0 && pendingApprovalIds.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(pendingApprovalIds);
    });
  };

  const openBulkApprove = () => {
    setBulkNote("");
    setBulkApproving(true);
  };

  const submitBulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error("Select at least one payment"); return; }
    if (!bulkNote.trim() || bulkNote.trim().length < 4) {
      toast.error("Approval note is required (min 4 chars)"); return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/payments/bulk-approve", {
        payment_ids: ids,
        note: bulkNote.trim(),
      });
      const ok = data.approved?.length || 0;
      const skipped = data.skipped?.length || 0;
      toast.success(`Approved ${ok} payment${ok === 1 ? "" : "s"}${skipped ? ` • ${skipped} skipped` : ""}`);
      setBulkApproving(false);
      setSelectedIds(new Set());
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkSaving(false);
    }
  };

  const rejectPayment = async (id) => {
    const reason = prompt("Rejection reason (required):");
    if (!reason) return;
    try { await api.post(`/payments/${id}/reject`, { reason }); toast.success("Payment rejected"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const overrideStatus = async (p) => {
    if (user?.role !== "admin") return;
    const NEXT = { paid: "unpaid", unpaid: "paid", pending: "paid", overdue: "paid", failed: "paid", refunded: "paid" };
    const choice = prompt(`Override payment status from "${p.payment_status}" to which one?\nOptions: paid, unpaid, pending, overdue, refunded, failed`, NEXT[p.payment_status] || "paid");
    if (!choice) return;
    const status = choice.toLowerCase().trim();
    if (!["paid", "unpaid", "pending", "overdue", "refunded", "failed"].includes(status)) {
      toast.error("Invalid status"); return;
    }
    const reason = prompt(`Why flip this payment to "${status}"? (required, audit-logged)`);
    if (!reason || !reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.patch(`/payments/${p.id}/status`, { status, reason });
      toast.success(`Status set to ${status}`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const totalCollected = payments.filter(p => p.payment_status === "paid").reduce((s,p) => s + (p.amount || 0), 0);
  const pending = payments.filter(p => p.payment_status === "pending").length;
  const overdue = payments.filter(p => p.payment_status === "overdue").length;
  const canManual = ["admin","manager"].includes(user.role);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Payments</h1>
          <p className="text-[#5E7166] text-sm">{payments.length} transactions • manual flow.</p>
        </div>
        {canManual && (
          <div className="flex flex-wrap gap-2">
            {selectedIds.size > 0 && (
              <button
                onClick={openBulkApprove}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0E5C3A] text-white hover:bg-[#0A4528] text-sm font-bold"
                data-testid="bulk-approve-open-btn"
              >
                <ListChecks size={16}/> Bulk Approve ({selectedIds.size})
              </button>
            )}
            <button onClick={openInvoice} className="btn-outline-emerald" data-testid="send-invoice-btn">
              <Send size={16}/> Create & Send Invoice
            </button>
            <button onClick={() => setAdding(true)} className="btn-gold" data-testid="record-payment-btn">
              <Plus size={16}/> Record Manual Payment
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Collected" value={naira(totalCollected)} icon={Wallet} accent/>
        <KPI label="Pending" value={pending} icon={Clock}/>
        <KPI label="Overdue" value={overdue} icon={AlertCircle}/>
        <KPI label="All Records" value={payments.length} icon={CreditCard}/>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modav-table">
            <thead><tr>
              {canManual && (
                <th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all pending-approval payments"
                    checked={pendingApprovalIds.length > 0 && pendingApprovalIds.every(id => selectedIds.has(id))}
                    onChange={toggleSelectAll}
                    disabled={pendingApprovalIds.length === 0}
                    data-testid="bulk-select-all"
                  />
                </th>
              )}
              <th>Ref</th><th>Tenant</th><th>Type</th><th>Amount</th><th>Status</th><th>Channel</th><th>Date</th><th></th>
            </tr></thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className={selectedIds.has(p.id) ? "bg-[#0E5C3A]/5" : ""}>
                  {canManual && (
                    <td>
                      {p.payment_status === "pending_approval" ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelect(p.id)}
                          aria-label={`Select payment ${p.id.slice(0,8)}`}
                          data-testid={`bulk-select-${p.id}`}
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="font-mono text-xs">{p.paystack_reference || p.id.slice(0,8)}</td>
                  <td>{p.tenant?.full_name || "—"}</td>
                  <td>{statusLabel(p.payment_type)}</td>
                  <td className="font-serif font-bold text-[#0E5C3A]">{naira(p.amount)}</td>
                  <td><span className={statusBadgeClass(p.payment_status)}>{statusLabel(p.payment_status)}</span></td>
                  <td>{p.channel}{p.manual_note && <div className="text-[10px] text-[#5E7166] truncate max-w-[150px]" title={p.manual_note}>{p.manual_note}</div>}</td>
                  <td className="text-xs">{formatDate(p.payment_date || p.created_at)}</td>
                  <td className="flex gap-1">
                    <a href={`${API_BASE}/payments/${p.id}/receipt`} target="_blank" rel="noreferrer" className="btn-outline-emerald text-xs" title="Receipt/Invoice PDF"><Download size={12}/></a>
                    {p.tenant_receipt_url && <a href={`${API_BASE}/files/view?path=${encodeURIComponent(p.tenant_receipt_url)}`} target="_blank" rel="noreferrer" className="btn-outline-emerald text-xs" title="Tenant uploaded receipt" data-testid={`view-tenant-receipt-${p.id}`}>📎</a>}
                    {p.payment_status === "pending_approval" && canManual && <>
                      <button onClick={() => approvePayment(p.id)} className="btn-gold text-xs" data-testid={`approve-${p.id}`}><CheckCircle2 size={12}/> Approve</button>
                      <button onClick={() => rejectPayment(p.id)} className="btn-ghost text-xs text-[#C92A2A]" data-testid={`reject-${p.id}`}>Reject</button>
                    </>}
                    {canManual && !p.tenant_receipt_url && (
                      <label
                        className="btn-outline-emerald text-xs cursor-pointer"
                        title={p.payment_status === "paid"
                          ? "Backfill a paper receipt onto this historical paid payment (audit-logged, status stays 'paid')"
                          : "Upload tenant's payment evidence on their behalf"}
                        data-testid={`upload-on-behalf-${p.id}`}
                      >
                        <Upload size={12}/>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => uploadOnBehalf(p, e)}
                        />
                      </label>
                    )}
                    {p.payment_status === "pending" && canManual && <button onClick={() => markAsPaid(p.id)} className="btn-outline-emerald text-xs" title="Mark paid manually"><CheckCircle2 size={12}/></button>}
                    {canManual && ["pending","overdue","pending_approval"].includes(p.payment_status) && (
                      <button onClick={() => sendReminder(p)} className="btn-outline-emerald text-xs" title="Email payment-due reminder (uses tenant email by default, can override)" data-testid={`remind-${p.id}`}>
                        <Send size={12}/>
                      </button>
                    )}
                    {user?.role === "admin" && <button onClick={() => overrideStatus(p)} title="Admin override status" className="text-[#7A6810] hover:bg-[#FDF6E3] p-1.5 rounded" data-testid={`override-${p.id}`}><Shield size={12}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {adding && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="manual-payment-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Record Manual Payment</h3>
              <button onClick={() => setAdding(false)}><X size={20}/></button>
            </div>
            <form onSubmit={createManual} className="space-y-3">
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Lease *</span>
                <select name="lease_id" required className="input-modav mt-1" data-testid="manual-lease">
                  <option value="">— Select the lease this payment is for —</option>
                  {leases.filter(l => l.lease_status !== "cancelled").map(l => (
                    <option key={l.id} value={l.id}>
                      {l.tenant?.full_name || "—"} • {l.unit?.unit_name || "?"} • {l.lease_start_date || "?"} → {l.lease_end_date || "?"}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-[#5E7166]">Tenant + unit are taken from the lease automatically.</span></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Payment Type</span>
                <select name="payment_type" required className="input-modav mt-1">
                  <option value="rent">Rent</option><option value="caution_fee">Caution Fee</option><option value="agreement_fee">Agreement Fee</option>
                  <option value="service_charge">Service Charge</option><option value="commission">Commission</option><option value="renewal">Renewal</option><option value="other">Other</option>
                </select></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Amount (₦)</span><input type="number" name="amount" required className="input-modav mt-1" data-testid="manual-amount"/></label>
              <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Note (required)</span><textarea name="note" required rows={3} className="input-modav mt-1" placeholder="Bank transfer ref, cash receipt, etc." data-testid="manual-note"/></label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="manual-submit-btn">Record Payment</button>
            </form>
          </div>
        </div>
      )}

      {invoicing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setInvoicing(false)}>
          <div className="card-modav p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-serif text-xl font-bold">Create & Send Invoice</h3>
                <p className="text-xs text-[#5E7166] mt-0.5">Creates a pending payment record + emails the tenant immediately.</p>
              </div>
              <button onClick={() => setInvoicing(false)}><X size={20}/></button>
            </div>
            <form onSubmit={submitInvoice} className="space-y-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Lease</span>
                <select name="lease_id" required className="input-modav mt-1" data-testid="invoice-lease">
                  <option value="">— Select lease —</option>
                  {invoiceLeases.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.tenant?.full_name || "—"} • {l.unit?.unit_name || "?"} • {l.lease_start_date || "?"} → {l.lease_end_date || "?"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Charge Type</span>
                <select name="payment_type" defaultValue="rent" required className="input-modav mt-1" data-testid="invoice-type">
                  <option value="rent">Rent</option><option value="renewal">Renewal Rent</option>
                  <option value="service_charge">Service Charge</option><option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Amount (₦)</span>
                <input type="number" min="1" step="any" name="amount" required className="input-modav mt-1" data-testid="invoice-amount"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Due Date</span>
                <input
                  type="date" name="due_date" required
                  defaultValue={new Date(Date.now()+7*86400000).toISOString().slice(0,10)}
                  className="input-modav mt-1" data-testid="invoice-due"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Note (shown to tenant)</span>
                <textarea name="note" rows={2} className="input-modav mt-1" placeholder="e.g. Year 2 rent for Hall — due 31 August" data-testid="invoice-note"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Override Email (optional)</span>
                <input type="email" name="email" className="input-modav mt-1" placeholder="Leave blank to use tenant's email on file" data-testid="invoice-email"/>
              </label>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="invoice-submit-btn">
                <Send size={14}/> Create & Send
              </button>
            </form>
          </div>
        </div>
      )}
      {bulkApproving && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !bulkSaving && setBulkApproving(false)}>
          <div className="card-modav p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="bulk-approve-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#0E5C3A]/10 text-[#0E5C3A] flex items-center justify-center">
                <ListChecks size={20}/>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl font-bold">Bulk Approve {selectedIds.size} Payment{selectedIds.size === 1 ? "" : "s"}</h3>
                <p className="text-xs text-[#5E7166] mt-1">Each payment is marked <b>paid</b>, gets a unique receipt number and the tenant receives an emailed receipt PDF.</p>
              </div>
              <button onClick={() => setBulkApproving(false)} disabled={bulkSaving}><X size={18}/></button>
            </div>

            <div className="bg-[#FDF6E3]/40 border border-[#EBDDA7] rounded-lg p-3 mb-3 max-h-48 overflow-y-auto">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A0801C] mb-2">Will approve:</div>
              <ul className="text-xs space-y-1">
                {payments.filter(p => selectedIds.has(p.id)).map(p => (
                  <li key={p.id} className="flex justify-between gap-3" data-testid={`bulk-preview-${p.id}`}>
                    <span className="truncate"><b>{p.tenant?.full_name || "—"}</b> · {statusLabel(p.payment_type)}</span>
                    <span className="font-mono font-bold text-[#0E5C3A] shrink-0">{naira(p.amount)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between text-xs font-bold mt-2 pt-2 border-t border-[#EBDDA7]">
                <span>Total</span>
                <span className="text-[#0E5C3A]" data-testid="bulk-total">
                  {naira(payments.filter(p => selectedIds.has(p.id)).reduce((s, p) => s + (Number(p.amount) || 0), 0))}
                </span>
              </div>
            </div>

            <label className="block mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">
                Approval Note <span className="text-[#C92A2A]">(required, audit-logged)</span>
              </span>
              <textarea
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                rows={2}
                placeholder="e.g. Verified bank statement for July rent batch"
                className="input-modav mt-1 resize-none"
                data-testid="bulk-approve-note"
              />
            </label>

            <div className="flex gap-2">
              <button onClick={() => setBulkApproving(false)} disabled={bulkSaving} className="btn-ghost flex-1 justify-center" data-testid="bulk-approve-cancel">Cancel</button>
              <button
                onClick={submitBulkApprove}
                disabled={bulkSaving || bulkNote.trim().length < 4}
                className="flex-1 justify-center inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0E5C3A] text-white hover:bg-[#0A4528] disabled:bg-[#5E7166] disabled:cursor-not-allowed text-sm font-bold"
                data-testid="bulk-approve-confirm"
              >
                <CheckCircle2 size={14}/> {bulkSaving ? "Approving…" : `Approve ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
